import { Component, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { DeviceTypeParameterConfigWithAvailableListAndParameter } from '@vectronic/util';

type Config = DeviceTypeParameterConfigWithAvailableListAndParameter;

@Component({
  selector: 'vectronic-parameter-time-config',
  templateUrl: './parameter-time-config.component.html',
  styleUrls: ['./parameter-time-config.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    ReactiveFormsModule,
    FormsModule,
    MatIcon,
    MatTooltip,
    MatIconButton,
    MatSuffix,
    MatSelect,
    MatOption,
  ],
})
export class ParameterTimeConfigComponent {
  config = input<Config>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();

  resetToFactoryTooltip = input<string>();
  hasClose = input(false);
  hideLabel = input<boolean>();
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();

  hourSelect = viewChild<MatSelect>('hourSelect');
  minuteSelect = viewChild<MatSelect>('minuteSelect');

  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  configClose = output();
  parameterChange = output<{
    config: Config;
    value: string;
  }>();
  hour: string;
  minute: string;
  calculatedValue: number;
  hasError = false;
  hours: string[] = Array.from({ length: 25 }, (_, i) => i.toString());
  minutes: string[] = Array.from({ length: 60 }, (_, i) => i.toString());

  valueSubscription = toObservable(this.value)
    .pipe(takeUntilDestroyed())
    .subscribe((val) => {
      if (!isNaN(parseInt(val))) {
        const convertedMinutes = this.convertMinutes(parseInt(val));
        this.hour = convertedMinutes.hours.toString();
        this.minute = convertedMinutes.minutes.toString();
      } else {
        this.hour = undefined;
        this.minute = undefined;
      }
      if (this.hourSelect()) {
        this.hourSelect().value = this.hour;
      }
      if (this.minuteSelect()) {
        this.minuteSelect().value = this.minute;
      }
    });

  onChangeHour($event: MatSelectChange) {
    this.hour = $event.toString();
    this.calculate();
  }

  onChangeMinute($event: MatSelectChange) {
    this.minute = $event.toString();
    this.calculate();
  }

  convertMinutes(seconds: number) {
    const minutes: number = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return { hours: hours, minutes: remainingMinutes };
  }

  calculate() {
    this.calculatedValue =
      parseInt(this.getValueAsParsableInt(this.hour)) * 3600 +
      parseInt(this.getValueAsParsableInt(this.minute)) * 60;
    const { validValueRange } = this.config().parameter;
    this.hasError = Boolean(
      (validValueRange?.max && this.calculatedValue > validValueRange.max) ||
      (validValueRange?.min && this.calculatedValue < validValueRange.min),
    );
    if (this.config().parsedMax && this.calculatedValue > parseInt(this.config().parsedMax)) {
      this.calculatedValue = parseInt(this.config().parsedMax);
      this.hour = Math.floor(this.calculatedValue / 3600).toString();
      this.minute = Math.floor((this.calculatedValue % 3600) / 60).toString();
      this.hourSelect().value = this.hour;
      this.minuteSelect().value = this.minute;
    }
    if (this.config().parsedMin && this.calculatedValue < parseInt(this.config().parsedMin)) {
      this.calculatedValue = parseInt(this.config().parsedMin);
      this.hour = Math.floor(this.calculatedValue / 3600).toString();
      this.minute = Math.floor((this.calculatedValue % 3600) / 60).toString();
      this.hourSelect().value = this.hour;
      this.minuteSelect().value = this.minute;
    }
    this.parameterChange.emit({
      config: this.config(),
      value: !isNaN(this.calculatedValue) ? this.calculatedValue.toString() : undefined,
    });
  }

  getValueAsParsableInt(value: string) {
    return value !== 'NaN' ? value || '0' : '0';
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
    this.hour = undefined;
    this.minute = undefined;
    this.hourSelect().value = undefined;
    this.minuteSelect().value = undefined;
  }

  get parameterTitle() {
    const { name, subcategory } = this.config().parameter;
    if (subcategory) return `${subcategory} / ${name}`;
    else return name;
  }

  onClickClose() {
    this.configClose.emit();
  }

  onClickUndo() {
    this.parameterUndo.emit(this.config());
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }
}
